//productSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";
import { filterPromotionsByCustomerId } from "utils/promotionFilterUtil";

// Helper to build URL and select method
const buildProductUrl = ({
  type,
  id,
  limit,
  offset = 0,
  sort,
  minPrice,
  maxPrice,
  searchByName,
  selectedCategories = "",
  selectedBrands = "",
  selectedManufacturers = "",
}) => {
  // Convert arrays to comma-separated strings for backend API
  const categoriesParam =
    Array.isArray(selectedCategories) && selectedCategories.length > 0
      ? selectedCategories.join(",")
      : selectedCategories || "";
  const brandsParam =
    Array.isArray(selectedBrands) && selectedBrands.length > 0
      ? selectedBrands.join(",")
      : selectedBrands || "";
  const manufacturersParam =
    Array.isArray(selectedManufacturers) && selectedManufacturers.length > 0
      ? selectedManufacturers.join(",")
      : selectedManufacturers || "";

  switch (type) {
    case "classification":
      return {
        url: endpoint.GET_ITEMS_BY_CLASS({
          classId: id,
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "brand":
      return {
        url: endpoint.GET_ITEMS_BY_BRAND({
          brand: id,
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "name":
      return {
        url: endpoint.POST_GET_ITEMS_BY_NAME({
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "post",
      };
    case "category":
      return {
        url: endpoint.GET_ITEMS_BY_CATEGORY({
          category: id,
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "all":
      return {
        url: endpoint.GET_ALL_PRODUCTS({
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "orderHistory":
      return {
        url: endpoint.GET_ITEMS_ORDER_HISTORY_BY_USER({
          userId: id,
          limit,
          offset,
          sort,
          searchByName,
        }),
        method: "get",
        requiresAuth: true,
      };
    case "favoriteItems":
      return {
        url: endpoint.POST_GET_ITEM_BY_IDS({
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "post",
      };
    case "promotion":
      return {
        url: endpoint.GET_PRODUCTS_WITH_ACTIVE_PROMOTIONS({
          limit,
          offset,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    default:
      throw new Error("Unknown product type");
  }
};

const buildCountUrl = ({
  type,
  id,
  minPrice,
  maxPrice,
  searchByName,
  selectedCategories = "",
  selectedBrands = "",
  selectedManufacturers = "",
}) => {
  // Convert arrays to comma-separated strings for backend API
  const categoriesParam =
    Array.isArray(selectedCategories) && selectedCategories.length > 0
      ? selectedCategories.join(",")
      : selectedCategories || "";
  const brandsParam =
    Array.isArray(selectedBrands) && selectedBrands.length > 0
      ? selectedBrands.join(",")
      : selectedBrands || "";
  const manufacturersParam =
    Array.isArray(selectedManufacturers) && selectedManufacturers.length > 0
      ? selectedManufacturers.join(",")
      : selectedManufacturers || "";

  switch (type) {
    case "classification":
      return {
        url: endpoint.GET_COUNT_BY_CLASS({
          classId: id,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "brand":
      return {
        url: endpoint.GET_COUNT_BY_BRAND({
          brand: id,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "name":
      return {
        url: endpoint.POST_GET_COUNT_BY_NAME({
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "post",
      };
    case "category":
      return {
        url: endpoint.GET_COUNT_BY_CATEGORY({
          category: id,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "all":
      return {
        url: endpoint.GET_COUNT_ALL_PRODUCTS({
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "promotion":
      return {
        url: endpoint.GET_COUNT_PRODUCTS_WITH_ACTIVE_PROMOTIONS({
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "get",
      };
    case "orderHistory":
      return {
        url: endpoint.GET_COUNT_USER_ORDER_HISTORY({
          userId: id,
          searchByName,
        }),
        method: "get",
        requiresAuth: true,
      };
    case "favoriteItems":
      return {
        url: endpoint.POST_GET_ITEM_BY_IDS({
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories: categoriesParam,
          selectedBrands: brandsParam,
          selectedManufacturers: manufacturersParam,
        }),
        method: "post",
        isCountFromLength: true, // Special flag to count from response length
      };
    default:
      throw new Error("Unknown type");
  }
};

// Async thunk to fetch a page of products
export const fetchProductsBy = createAsyncThunk(
  "products/fetchPage",
  async (
    {
      type,
      id,
      page = 1,
      limit = 12,
      sort,
      minPrice,
      maxPrice,
      searchByName,
      selectedCategories = "",
      selectedBrands = "",
      selectedManufacturers = "",
      getAccessTokenSilently,
    },
    { rejectWithValue, getState, signal }
  ) => {
    try {
      const offset = (page - 1) * limit;

      const { url, method, requiresAuth } = buildProductUrl({
        type,
        id,
        limit,
        offset,
        sort,
        minPrice,
        maxPrice,
        searchByName,
        selectedCategories,
        selectedBrands,
        selectedManufacturers,
      });
      let res;

      // Set up headers for authenticated requests
      const headers = {};
      if (requiresAuth && getAccessTokenSilently) {
        try {
          const token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch (tokenError) {
          return rejectWithValue("Failed to get authentication token");
        }
      }

      // axios honours config.signal, and axios-retry 4.x lists ERR_CANCELED in
      // its CODE_EXCLUDE_LIST, so an aborted request is not silently retried.
      switch (method) {
        case "post":
          switch (type) {
            case "favoriteItems":
              res = await api.post(url, { ids: id }, { headers, signal });
              break;
            case "name":
              res = await api.post(url, { name: id }, { headers, signal });
              break;
            default:
              res = await api.post(url, { name: id }, { headers, signal });
              break;
          }
          break;
        case "get":
        default:
          res = await api.get(url, { headers, signal });
          break;
      }

      // Get user category from Redux state to filter promotions
      const state = getState();
      // eslint-disable-next-line no-unused-vars
      const userCategory = state?.user?.info?.category;

      // Filter products based on customer category restriction
      const products = filterPromotionsByCustomerId(res.data.items || res.data);

      return {
        id,
        page,
        products,
        total: res.data.total || 0,
        limit,
        sort,
        minPrice,
        maxPrice,
        searchByName,
        selectedCategories,
        selectedBrands,
        selectedManufacturers,
        type,
      };
    } catch (err) {
      // A superseded page request was aborted, not failed. Rethrow so RTK tags
      // the action `meta.aborted` instead of letting rejectWithValue turn a
      // cancellation into a user-visible error.
      if (signal.aborted || err?.code === "ERR_CANCELED") throw err;
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// Async thunk to fetch count of products
export const fetchCountBy = createAsyncThunk(
  "products/fetchCountBy",
  async (
    {
      type,
      id,
      minPrice,
      maxPrice,
      searchByName,
      selectedCategories = "",
      selectedBrands = "",
      selectedManufacturers = "",
      getAccessTokenSilently,
    },
    { rejectWithValue }
  ) => {
    try {
      const { url, method, requiresAuth, isCountFromLength } = buildCountUrl({
        type,
        id,
        minPrice,
        maxPrice,
        searchByName,
        selectedCategories,
        selectedBrands,
        selectedManufacturers,
      });

      // Set up headers for authenticated requests
      const headers = {};
      if (requiresAuth && getAccessTokenSilently) {
        try {
          const token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch (tokenError) {
          return rejectWithValue("Failed to get authentication token");
        }
      }

      let res;
      if (method === "post") {
        if (type === "favoriteItems") {
          res = await api.post(url, { ids: id }, { headers });
        } else {
          res = await api.post(url, { name: id }, { headers });
        }
      } else {
        res = await api.get(url, { headers });
      }

      // For favoriteItems, count from response length; for orderHistory use count endpoint
      let count;
      if (isCountFromLength) {
        const items = res.data.items || res.data;
        count = Array.isArray(items) ? items.length : 0;
      } else {
        count = res.data.count;
      }

      return {
        id: id || "all",
        count,
        minPrice,
        maxPrice,
        searchByName,
        selectedCategories,
        selectedBrands,
        selectedManufacturers,
        type,
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);
const productsSlice = createSlice({
  name: "products",
  initialState: {
    productsByPage: {}, // key: `${id}_${limit}_${sort}_${priceKey}_${page}` => products[]
    totalCounts: {}, // key: `${id}_${priceKey}` => total count (where id can be classId, brandId, "all", etc.)
    isLoading: false,
    // requestId of the newest fetchProductsBy. Pagination stays clickable while a
    // page loads, so requests overlap; only the newest one may clear isLoading.
    // Otherwise whichever request lands first hides the skeleton and the user
    // stares at an empty grid while the page they asked for is still in flight.
    currentRequestId: null,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductsBy.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchProductsBy.fulfilled, (state, action) => {
        // The page is cached either way: productsByPage is keyed, so a late
        // arrival writes its own slot and can never clobber the current page.
        if (state.currentRequestId === action.meta.requestId) {
          state.isLoading = false;
          state.currentRequestId = null;
        }
        const {
          id,
          page,
          products,
          total,
          limit,
          sort,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories,
          selectedBrands,
          selectedManufacturers,
          type,
        } = action.payload;
        const priceKey = `${minPrice || ""}_${maxPrice || ""}`;
        const searchKey = searchByName ? encodeURIComponent(searchByName) : "";
        // Convert arrays to comma-separated strings for backend, use % for cache keys
        const categoriesForCache =
          Array.isArray(selectedCategories) && selectedCategories.length > 0
            ? selectedCategories.join("%")
            : selectedCategories || "";
        const brandsForCache =
          Array.isArray(selectedBrands) && selectedBrands.length > 0
            ? selectedBrands.join("%")
            : selectedBrands || "";
        const manufacturersForCache =
          Array.isArray(selectedManufacturers) &&
          selectedManufacturers.length > 0
            ? selectedManufacturers.join("%")
            : selectedManufacturers || "";

        // Handle array IDs for favoriteItems
        let effectiveId;
        if (type === "favoriteItems" && Array.isArray(id)) {
          effectiveId =
            id.length > 0
              ? [...id].sort((a, b) => a - b).join(",")
              : "empty-favorites";
        } else {
          effectiveId = id || "all";
        }

        // All requests now use pagination
        const key = `${effectiveId}_${limit || 12}_${sort}_${priceKey}_${searchKey}_${categoriesForCache}_${brandsForCache}_${manufacturersForCache}_${page}`;
        const countKey = `${effectiveId}_${priceKey}_${searchKey}_${categoriesForCache}_${brandsForCache}_${manufacturersForCache}`;

        state.productsByPage[key] = products;

        // Store the total count if it's provided in the response
        if (total && total > 0) {
          state.totalCounts[countKey] = total;
        }
      })
      .addCase(fetchProductsBy.rejected, (state, action) => {
        // Aborted means superseded by a newer page request, which already owns
        // isLoading. Not an error, and not ours to clear.
        if (action.meta.aborted || action.error?.code === "ERR_CANCELED")
          return;
        if (state.currentRequestId === action.meta.requestId) {
          state.isLoading = false;
          state.currentRequestId = null;
        }
        state.error = action.payload;
      })
      .addCase(fetchCountBy.fulfilled, (state, action) => {
        const {
          id,
          count,
          minPrice,
          maxPrice,
          searchByName,
          selectedCategories,
          selectedBrands,
          selectedManufacturers,
          type,
        } = action.payload;
        const priceKey = `${minPrice || ""}_${maxPrice || ""}`;
        const searchKey = searchByName ? encodeURIComponent(searchByName) : "";
        // Use same format as in fetchProductsBy for consistent cache keys
        const categoriesForCache =
          Array.isArray(selectedCategories) && selectedCategories.length > 0
            ? selectedCategories.join("%")
            : selectedCategories || "";
        const brandsForCache =
          Array.isArray(selectedBrands) && selectedBrands.length > 0
            ? selectedBrands.join("%")
            : selectedBrands || "";
        const manufacturersForCache =
          Array.isArray(selectedManufacturers) &&
          selectedManufacturers.length > 0
            ? selectedManufacturers.join("%")
            : selectedManufacturers || "";
        let effectiveId;
        if (type === "favoriteItems" && Array.isArray(id)) {
          effectiveId =
            id.length > 0
              ? [...id].sort((a, b) => a - b).join(",")
              : "empty-favorites";
        } else {
          effectiveId = id || "all";
        }
        const countKey = `${effectiveId}_${priceKey}_${searchKey}_${categoriesForCache}_${brandsForCache}_${manufacturersForCache}`;
        state.totalCounts[countKey] = count;
      });
  },
});

export default productsSlice.reducer;
